import { Button, Grid, Heading, Input, Text } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import React, { ChangeEvent, createRef, FormEvent, FunctionComponent, useEffect, useState } from 'react'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { ACCEPTABLE_PASSWORD_STRENGTH } from '../../model'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { DDPrivacyLevel } from '@afterpaytouch/integrations/datadog'
import { useConsumerSizes } from '../../utils/responsive'
import { ProfileCard } from '../ProfileCard'
import { useAuthResetMutation, useConsumerEmail, useConsumerName, usePasswordResetVerifyCode } from '../../state'
import { useRouter } from 'next/router'
import { Route } from '../../config/router'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'

const errorMessageMap: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.WeakPassword]: 'createPassword:error:weakPassword',
  [ErrorCode.IdenticalPassword]: 'createPassword:error:identicalPassword',
  [ErrorCode.InvalidNewPassword]: 'createPassword:error:weakPassword',
  [ErrorCode.RecentPassword]: 'createPassword:error:identicalPassword',
}

export const CreatePassword: FunctionComponent = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    password: '',
    'remember-me': false,
  })
  const consumerName = useConsumerName()
  const [errorMessage, setErrorMessage] = useState('')
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [locked, setLocked] = useState(false)
  const inputRef = createRef<HTMLInputElement>()
  const { headingSize, textSize } = useConsumerSizes()
  const email = useConsumerEmail() ?? ''
  const code = usePasswordResetVerifyCode()
  const [authReset] = useAuthResetMutation()
  const clearError = (): void => setErrorMessage('')
  const lock = (): void => setLocked(true)
  const isError = errorMessage !== ''
  const router = useRouter()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  useEffect(() => {
    if (passwordStrength >= ACCEPTABLE_PASSWORD_STRENGTH) {
      clearError()
    }
  }, [passwordStrength])

  const onFormChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    const isCheckbox = ev.target.type === 'checkbox'

    setFormData({
      ...formData,
      [ev.target.name]: isCheckbox ? ev.target.checked : ev.target.value,
    })
  }

  const handleSubmit = async (ev: FormEvent): Promise<void> => {
    ev.preventDefault()
    if (formData.password === '') {
      logEvent(TrackingEvent.CLICKED_CONTINUE_ON_CREATE_PASSWORD, {
        error: 'empty password',
      })
      return setErrorMessage(t('createPassword:error:emptyPassword'))
    }
    if (passwordStrength < ACCEPTABLE_PASSWORD_STRENGTH) {
      logEvent(TrackingEvent.CLICKED_CONTINUE_ON_CREATE_PASSWORD, {
        error: 'password too weak',
      })
      return setErrorMessage(t('createPassword:error:weakPassword'))
    }
    logEvent(TrackingEvent.CLICKED_CONTINUE_ON_CREATE_PASSWORD)
    clearError()
    lock()
    try {
      await authReset({ code, email, ...formData }).unwrap()
      setLocked(false)
      router.push(Route.LOGGED_IN)
    } catch (e) {
      const errorMessageKey = errorMessageMap[e.data.errorCode] ?? 'password:error:unknown'
      setErrorMessage(t(errorMessageKey))
      setLocked(false)
    }
  }

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  return (
    <form onSubmit={handleSubmit}>
      <Grid>
        <Heading size={headingSize}>{t('createPassword:heading')}</Heading>
      </Grid>
      <div className='mb-4 mt-4'>
        <ProfileCard consumerName={consumerName} consumerEmail={email ?? ''} />
      </div>
      <div className='mb-4'>
        <Text size={textSize}>{t('createPassword:description')}</Text>
      </div>
      <Grid>
        <MaskedElement stylingModifier='wrap' privacyLevel={DDPrivacyLevel.Hidden} actionName='Create Password Input'>
          <Input.Password
            ref={inputRef}
            onChange={onFormChange}
            label={t('createPassword:inputLabel')}
            testNameSpace='create-password'
            name='password'
            autoComplete='new-password'
            aria-labelledby='createPasswordLabel'
            defaultPasswordMasked={false}
            passwordStrengthLabel={['weak', 'good', 'great'].map((s) => t('createPassword:passwordStrength', { context: s }))}
            onPasswordStrengthChange={setPasswordStrength}
            disabled={locked}
            error={isError}
            errorText={errorMessage}
          />
        </MaskedElement>
      </Grid>
      <Grid>
        <Grid.Item>
          <Button.Primary type='submit' testNameSpace='create-password' disabled={locked} padding='Fluid'>
            {t('common:continue')}
          </Button.Primary>
        </Grid.Item>
      </Grid>
    </form>
  )
}
